import React from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { css } from '@emotion/react';
import { PrimaryHeader } from '@/styles/typography';
import {
    CenteredSpinner,
    CycleCountBreadcrumbs,
    DetailHeaderBar,
    DetailNotFound,
} from '@/components/CycleCounts/blocks';
import { formatDispositionText } from '@/components/CycleCounts/utils';
import { useCycleCountLocationDetails } from './hooks';

const CycleCountLocation = () => {
    const navigate = useNavigate();
    const { cycle_count_location_id: cycleCountLocationId } = useParams();
    const { countLocation, rows, loading } = useCycleCountLocationDetails(cycleCountLocationId);

    if (loading && !countLocation) {
        return <CenteredSpinner />;
    }

    if (!countLocation) {
        return (
            <DetailNotFound
                title="Location Not Found"
                message="This cycle count location could not be loaded."
            />
        );
    }

    const locationTitle = countLocation.warehouse_location || 'Cycle Count Location';
    const warehouseName = countLocation.cycle_count?.warehouse?.location_name || 'Warehouse';
    const cycleCountId = countLocation.cycle_count?.cycle_count_id;

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <DetailHeaderBar container direction="row">
                <CycleCountBreadcrumbs
                    trail={[
                        { label: 'Cycle Counts', to: '/cycle-counts' },
                        { label: warehouseName, to: cycleCountId ? `/cycle-counts/${cycleCountId}` : null },
                        { label: locationTitle },
                    ]}
                />
            </DetailHeaderBar>

            <Grid
                container
                direction="column"
                css={css`
                    padding: 2rem;
                    gap: 1rem;
                    flex-wrap: nowrap;
                    overflow-y: auto;
                `}
            >
                <PrimaryHeader>{locationTitle}</PrimaryHeader>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>SKU</TableCell>
                                <TableCell>PO Number</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Location</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography color="textSecondary">
                                            No items to display for this location.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow
                                        key={row.key}
                                        hover
                                        css={css`cursor: pointer;`}
                                        onClick={() =>
                                            cycleCountId &&
                                            cycleCountLocationId &&
                                            row.item_id &&
                                            navigate(
                                                `/cycle-counts/${cycleCountId}/locations/${cycleCountLocationId}/items/${row.item_id}`
                                            )
                                        }
                                    >
                                        <TableCell>{row.sku || '-'}</TableCell>
                                        <TableCell>{row.po_number || '-'}</TableCell>
                                        <TableCell>{row.description || '-'}</TableCell>
                                        <TableCell style={{ color: row.disposition.color }}>
                                            {formatDispositionText(row.disposition)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
};

export default CycleCountLocation;
